import { Camera } from "../../../../SharedGL/Camera";
import { IGroundEvent, IHighlightFlightSettings } from "@vmosolutions/oc-interfaces";
import { GroundEventShader } from "./GroundEventShader";
import { hexToRgb, findClosestGroundIndex } from "../../TimelineUtils";
import { FlightGL } from "../Flight/FlightGL";
import { GroundEventBuffer } from "./GroundEventBuffer";

interface IGroundEventGLAttributes {
  position: WebGLBuffer;
  color: WebGLBuffer;
}

export class GroundEventGL {
  public static aircraftRowTotalHeight: number = FlightGL.aircraftRowHeight + FlightGL.aircraftRowPadding;

  public static delayedTimeMillis = 900000; // 15 minutes

  public static readonly referenceTime = new Date().getTime();

  private groundEvents: GroundEventBuffer;
  private groundEventsPriority: GroundEventBuffer;

  protected buffers: IGroundEventGLAttributes;

  private vertexCount = 0;
  private priorityVertexCount = 0;

  private groundEventIndexesInRange: number[] = [0, 0];
  private groundEventsInRange: IGroundEvent[] = [];

  private data: IGroundEvent[] = [];
  constructor(gl: WebGLRenderingContext, groundEventShader: GroundEventShader, texture: WebGLTexture) {
    // const texture = loadTexture(
    //   gl,
    //   "/assets/timeline-icons/diagonal-pattern-256.png"
    // );
    this.groundEvents = new GroundEventBuffer(gl, groundEventShader, texture);
    this.groundEventsPriority = new GroundEventBuffer(gl, groundEventShader, texture);
  }

  public updateBuffers = (
    gl: WebGLRenderingContext,
    data: IGroundEvent[],
    highlightFlightSettings?: IHighlightFlightSettings
  ): void => {
    this.data = data;
    this.groundEventIndexesInRange = [0, data.length];

    const actualGroundEventPositions: number[] = [];
    const actualGroundEventColors: number[] = [];

    const priorityGroundEventPositions: number[] = [];
    const priorityGroundEventColors: number[] = [];

    const groundEventAlpha =
      highlightFlightSettings?.active &&
      !!highlightFlightSettings?.conditions &&
      (highlightFlightSettings.conditions?.flightNumber?.length > 0 ||
        highlightFlightSettings.conditions?.origin?.length > 0 ||
        highlightFlightSettings.conditions?.flightType?.length > 0 ||
        highlightFlightSettings.conditions?.destination?.length > 0 ||
        highlightFlightSettings.conditions?.dispatchDesk?.length > 0 ||
        highlightFlightSettings.conditions?.highlightUnassigned)
        ? highlightFlightSettings?.dimmSetting
        : 1.0;

    // iterating over an aircraft (row) so height will always be same
    const priorityYStart = FlightGL.scheduledStart;
    const priorityYEnd = priorityYStart + FlightGL.scheduledHeight;

    let y0 = FlightGL.scheduledStart; //priorityYEnd;
    const y = FlightGL.actualDepartureStart + FlightGL.actualDepartureHeight;

    data.forEach((groundEvent: IGroundEvent) => {
      //If the ground event has a priority color, then we need to draw a bar for it so adjust where the reg block starts
      if (groundEvent.custom?.priority) {
        y0 = priorityYEnd;
      }
      // positions
      const x0 = new Date(groundEvent.actualStart).getTime() - Camera.referenceTime;
      const x = new Date(groundEvent.actualEnd).getTime() - Camera.referenceTime;
      const positions = [x0, y0, x, y0, x, y, x0, y0, x, y, x0, y];
      actualGroundEventPositions.push(...positions);
      // colors:
      const [r, g, b] = groundEvent.eventType ? hexToRgb(groundEvent.eventType.color) : [1, 1, 1];
      const colors = [
        r,
        g,
        b,
        groundEventAlpha,
        r,
        g,
        b,
        groundEventAlpha,
        r,
        g,
        b,
        groundEventAlpha,
        r,
        g,
        b,
        groundEventAlpha,
        r,
        g,
        b,
        groundEventAlpha,
        r,
        g,
        b,
        groundEventAlpha,
      ];

      actualGroundEventColors.push(...colors);

      if (groundEvent.custom?.priority) {
        const [r, g, b] = groundEvent.custom?.priority ? hexToRgb(groundEvent.custom?.priority) : [1, 1, 1];
        const colors = [
          r,
          g,
          b,
          groundEventAlpha,
          r,
          g,
          b,
          groundEventAlpha,
          r,
          g,
          b,
          groundEventAlpha,
          r,
          g,
          b,
          groundEventAlpha,
          r,
          g,
          b,
          groundEventAlpha,
          r,
          g,
          b,
          groundEventAlpha,
        ];

        priorityGroundEventColors.push(...colors);

        // positions
        const positions = [
          x0,
          priorityYStart,
          x,
          priorityYStart,
          x,
          priorityYEnd,
          x0,
          priorityYStart,
          x,
          priorityYEnd,
          x0,
          priorityYEnd,
        ];
        priorityGroundEventPositions.push(...positions);
      }
    });

    this.groundEvents.updateBuffers(gl, actualGroundEventPositions, actualGroundEventColors);
    this.vertexCount = actualGroundEventPositions.length / 2;
    this.groundEventsPriority.updateBuffers(gl, priorityGroundEventPositions, priorityGroundEventColors);
    this.priorityVertexCount = priorityGroundEventPositions?.length / 2;
  };

  public getGroundEventIndexes = (): IGroundEvent[] => {
    return this.groundEventsInRange;
  };

  public updateGroundEventsInRange = (camera: Camera): number[] => {
    if (this.data.length === 0) {
      return [0, 0];
    }
    const [minTime, maxTime] = camera.getXRange();
    const [min, max] = findClosestGroundIndex(this.data, [minTime, maxTime], ["actualStart", "actualEnd"]);
    this.groundEventIndexesInRange = [Math.max(0, min - 1), Math.min(max + 1, this.data.length - 1)];
    return this.groundEventIndexesInRange;
  };

  public getGroundEventsInRange = (): IGroundEvent[] => {
    return this.groundEventsInRange;
  };

  public getGroundEventIndexesInRange = (): number[] => {
    return this.groundEventIndexesInRange;
  };

  public getGroundEvents = (): IGroundEvent[] => {
    return this.data;
  };

  public draw = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    const [minIndex, maxIndex] = this.groundEventIndexesInRange;
    if (this.vertexCount === 0 || minIndex < 0 || maxIndex < 0) {
      return;
    }

    const [minX, maxX] = camera.getXRange();

    const xScale = gl.canvas.width / (maxX - Camera.referenceTime - (minX - Camera.referenceTime));
    const scale = [xScale, 0.01666]; // y is approx (FlightGL.aircraftRowTotalHeight / 2.0)

    const offset = minIndex * 6;
    const numVerts = (maxIndex + 1) * 6 - offset;
    if (numVerts > 0 && numVerts <= this.vertexCount) {
      this.groundEvents.draw(gl, camera, yStart, offset, numVerts, scale);
    }

    //Only Draw the Priority Ground Events Bar if there is some for this row
    if (this.priorityVertexCount === 0) {
      return;
    }
    this.groundEventsPriority.draw(gl, camera, yStart, 0, this.priorityVertexCount, scale);
  };
}
